import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus, ArrowUp, Loader2, X, Check, ChevronDown, Upload,
  Camera, Package, Ratio, FileText, AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getRegionDisplay } from "@/lib/regions";
import { findMismatches, type ConfigLink } from "@/lib/sceneCompatibility";
import { costWithModel } from "../../../../../supabase/functions/_shared/rateCard.ts";
import { IMAGE_MODEL_OPTIONS, imageModelsForSize } from "@/lib/aiModels";
import type { StudioAttachment, StudioSettings, StudioConfig } from "@/hooks/useStudioSession";

/** Aspect ratios Gemini accepts, grouped so the menu stays readable. */
const ASPECT_GROUPS: { label: string; ratios: string[] }[] = [
  { label: "Square & feed", ratios: ["1:1", "4:5", "5:4"] },
  { label: "Landscape", ratios: ["16:9", "3:2", "4:3", "21:9"] },
  { label: "Portrait", ratios: ["9:16", "2:3", "3:4"] },
];

const SIZES = ["0.5K", "1K", "2K", "4K"];
const SIZE_RANK: Record<string, number> = { "0.5K": 0, "1K": 1, "2K": 2, "4K": 3 };

interface CameraOption { id: string; name: string }
interface ProductOption {
  id: string; name: string;
  thumbnailUrl?: string | null;
  /** The product *type*, which is what a scene is linked to. */
  configId?: string | null;
}
interface BriefOption { id: string; name: string; regionKey: string | null; config?: any }

interface StudioComposerProps {
  settings: StudioSettings;
  config: StudioConfig;
  cameras: CameraOption[];
  products: ProductOption[];
  briefs: BriefOption[];
  /** Opens the scene picker — the same product-then-scene-then-camera flow
   *  used to start a session, so a scene is set up one way only. */
  onAddScene: () => void;
  /** True once the session has produced an image — follow-ups build on it. */
  hasPreviousImage?: boolean;
  /** config_links rows, so a pairing that stopped fitting can be noticed. */
  links: ConfigLink[];
  isGenerating: boolean;
  creditsPerImage: number;
  onSettingsChange: (patch: Partial<StudioSettings>) => void;
  onUpload: (file: File) => Promise<StudioAttachment | null>;
  onSubmit: (prompt: string, attachments: StudioAttachment[]) => void;
}

export function StudioComposer({
  settings, config, cameras, products, briefs, onAddScene, links, hasPreviousImage,
  isGenerating, creditsPerImage,
  onSettingsChange, onUpload, onSubmit,
}: StudioComposerProps) {
  const [prompt, setPrompt] = useState("");
  const [attachments, setAttachments] = useState<StudioAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  // Grow the textarea with its content rather than scrolling a fixed box.
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [prompt]);

  // Only offer models the tenant permits AND that can produce the chosen size
  // (Flash Lite is 1K-only).
  const availableModels = imageModelsForSize(
    settings.resolution === "0.5K" ? "1K" : (settings.resolution as "1K" | "2K" | "4K"),
  ).filter((m) => config.allowed_models.length === 0 || config.allowed_models.includes(m.id));

  const selectedModel =
    availableModels.find((m) => m.id === settings.model) || availableModels[0];

  const maxSize = SIZE_RANK[config.max_resolution] ?? 3;
  const availableSizes = SIZES.filter((s) => SIZE_RANK[s] <= maxSize);

  const selectedCamera = cameras.find((c) => c.id === settings.cameraConfigId);
  const selectedProduct = products.find((p) => p.id === settings.productId);

  // A scene already contains a complete prompt, so free text is optional when
  // one is selected — requiring it would block generation after the user has
  // answered every question.
  const selectedBrief = briefs.find((b) => b.id === settings.briefConfigId);
  const hasScene = !!settings.briefConfigId;

  // A scene is a product photographed a particular way, so it needs both. The
  // product requirement used to depend on the template mentioning a product,
  // which meant it appeared for some scenes and not others with no way for the
  // user to predict which. Both are now required for every scene, matching what
  // the start dialog asks for, and are checked here as well as server-side so
  // the user is told before spending a click rather than after.
  const missingProduct = hasScene && !settings.productId;
  const missingCamera = hasScene && !settings.cameraConfigId;
  const sceneIncomplete = [
    missingProduct && "a product",
    missingCamera && "a camera style",
  ].filter(Boolean) as string[];

  // Any of these supplies prompt direction on its own — a brief is a complete
  // instruction, and a camera style or product each contribute their own
  // sections server-side. Requiring free text on top meant a fully configured
  // generation still had a dead Generate button.
  const hasDirection =
    prompt.trim().length > 0 ||
    hasScene ||
    !!settings.cameraConfigId ||
    !!settings.productId;

  const canSubmit =
    hasDirection && sceneIncomplete.length === 0 && !isGenerating && !uploading;

  /**
   * Selected sources, shown as removable tags inside the prompt box.
   *
   * They feed the prompt server-side, so they belong with the prompt rather
   * than in the row of render parameters. Each one's picker drops out of the
   * control bar while it is selected — otherwise the same choice would be
   * visible in two places at once.
   */
  const sourceTags = [
    selectedBrief && {
      key: "brief",
      onOpen: onAddScene,
      // The flag rather than a document icon: a scene is written for one
      // market, and "Norway" is the part of it you cannot infer from the name.
      icon: <span aria-hidden className="text-[11px] leading-none">
        {getRegionDisplay(selectedBrief.regionKey).emoji}
      </span>,
      label: selectedBrief.name,
      title: `${getRegionDisplay(selectedBrief.regionKey).label} scene`,
      onRemove: () => onSettingsChange({ briefConfigId: null, briefAnswers: {} }),
    },
    selectedCamera && {
      key: "camera",
      onOpen: hasScene ? onAddScene : undefined,
      icon: <Camera className="w-3 h-3" />,
      label: selectedCamera.name,
      onRemove: () => onSettingsChange({ cameraConfigId: null }),
    },
    selectedProduct && {
      key: "product",
      onOpen: hasScene ? onAddScene : undefined,
      icon: <Package className="w-3 h-3" />,
      label: selectedProduct.name,
      onRemove: () => onSettingsChange({ productId: null }),
    },
  ].filter(Boolean) as {
    key: string; icon: JSX.Element; label: string; title?: string;
    onRemove: () => void; onOpen?: () => void;
  }[];

  /**
   * What no longer fits. A scene records the product types it is written for
   * and the camera styles it supports, and the start dialog narrows each
   * choice to the next — but a product changed afterwards, from the + menu,
   * can leave a pairing the scene was never written for. An outdoor unit in an
   * indoor scene produces a plausible image of the wrong thing, and costs
   * credits to find out.
   */
  const mismatches = findMismatches({
    links,
    sceneId: settings.briefConfigId,
    productConfigId: selectedProduct?.configId,
    cameraId: settings.cameraConfigId,
  });

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      const att = await onUpload(file);
      if (att) setAttachments((prev) => [...prev, att]);
    }
    setUploading(false);
  };

  const submit = () => {
    if (!canSubmit) return;
    onSubmit(prompt.trim(), attachments);
    setPrompt("");
    setAttachments([]);
  };

  return (
    <div className="rounded-2xl border border-border bg-card/80 backdrop-blur-sm shadow-lg">
      {/* Attachment chips */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 px-4 pt-3">
          {attachments.map((a, i) => (
            <div key={i} className="relative group">
              <img
                src={a.url}
                alt={a.name}
                className="h-14 w-14 rounded-lg object-cover border border-border"
              />
              <button
                onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-background border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive"
                aria-label={`Remove ${a.name}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Attach + prompt */}
      <div className="flex items-start gap-2 px-3 pt-3">
        {/* One button for "bring something in", rather than an upload button
            here and a product picker in the control bar below. The two are the
            same intent — attach a reference — and splitting them put half the
            answer out of sight. */}
        {(config.allow_uploads || (config.allow_product_attach && products.length > 0)) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full shrink-0"
                disabled={uploading || isGenerating}
                aria-label="Add a photo or a product"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60 max-h-80 overflow-y-auto">
              {config.allow_uploads && (
                <DropdownMenuItem onClick={() => fileRef.current?.click()} className="gap-2 cursor-pointer">
                  <Upload className="w-3.5 h-3.5 shrink-0" />
                  <span>Upload a photo</span>
                </DropdownMenuItem>
              )}
              {config.allow_product_attach && products.length > 0 && (
                <>
                  {config.allow_uploads && <DropdownMenuSeparator />}
                  <DropdownMenuLabel className="text-xs">Choose a product</DropdownMenuLabel>
                  {products.map((p) => (
                    <DropdownMenuItem
                      key={p.id}
                      onClick={() => onSettingsChange({ productId: settings.productId === p.id ? null : p.id })}
                      className="gap-2 cursor-pointer"
                    >
                      <span className="w-4 shrink-0">
                        {settings.productId === p.id && <Check className="w-3.5 h-3.5" />}
                      </span>
                      {/* Several products differ only by a colour word. */}
                      <span className="w-6 h-6 rounded bg-muted overflow-hidden shrink-0 flex items-center justify-center">
                        {p.thumbnailUrl
                          ? <img src={p.thumbnailUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
                          : <Package className="w-3 h-3 text-muted-foreground/50" />}
                      </span>
                      <span className="truncate">{p.name}</span>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <div className="flex-1 min-w-0">
          {sourceTags.length > 0 && (
            <div className="flex flex-wrap gap-1 pb-1.5">
              {sourceTags.map((t) => (
                <span
                  key={t.key}
                  title={t.title}
                  className="inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-md bg-primary/10 text-primary text-[11px] font-medium"
                >
                  {t.icon}
                  {/* The tag is the way back to the thing it names. Clicking
                      "Technical Interior" to change the camera style is the
                      obvious move, and it used to do nothing. */}
                  {t.onOpen ? (
                    <button
                      onClick={t.onOpen}
                      disabled={isGenerating}
                      title="Open the scene setup"
                      className="max-w-[10rem] truncate underline decoration-primary/30 underline-offset-2 hover:decoration-primary disabled:no-underline"
                    >
                      {t.label}
                    </button>
                  ) : (
                    <span className="max-w-[10rem] truncate">{t.label}</span>
                  )}
                  <button
                    onClick={t.onRemove}
                    disabled={isGenerating}
                    aria-label={`Remove ${t.label}`}
                    className="w-4 h-4 rounded flex items-center justify-center hover:bg-primary/20 disabled:opacity-40"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        <textarea
          ref={textRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); }
          }}
          rows={1}
          placeholder={hasPreviousImage
            // A typed follow-up now carries the session's latest image along,
            // so this is true iteration, not a fresh roll.
            ? "Describe what to change — builds on the latest image…"
            : hasScene
              ? "Describe the shot — the scene fills in the rest…"
              : "Describe the image you want to create…"}
          className="w-full resize-none bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none py-1.5 min-h-[32px]"
          disabled={isGenerating}
        />
        </div>
        {/* Inline with the prompt rather than in the bottom bar, which sits
            under the floating chat widget in the corner. */}
        <Button
          size="icon"
          className="h-8 w-8 rounded-full gradient-primary text-primary-foreground shrink-0"
          onClick={submit}
          disabled={!canSubmit}
          aria-label="Generate"
        >
          {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
        </Button>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => { void handleFiles(e.target.files); e.target.value = ""; }}
      />

      {/* Informs and offers the fix, rather than blocking. The combination is
          allowed — a scene's links are guidance about what it was written for,
          not a rule — but silently producing the wrong thing is worse than a
          sentence saying so. */}
      {mismatches.length > 0 && selectedBrief && (
        <div className="mx-3 mt-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2">
          <p className="flex items-start gap-1.5 text-[11px] leading-snug text-amber-900 dark:text-amber-200">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
            <span>
              {mismatches.some((m) => m.kind === "product") && (
                <>
                  <span className="font-medium">{selectedProduct?.name}</span>
                  {" is not a product "}
                  <span className="font-medium">{selectedBrief.name}</span>
                  {" was written for"}
                  {mismatches.length > 1 ? ", and " : ". "}
                </>
              )}
              {mismatches.some((m) => m.kind === "camera") && (
                <>
                  <span className="font-medium">{selectedCamera?.name}</span>
                  {" is not one of its camera styles. "}
                </>
              )}
              It will still generate — expect a plausible image of the wrong thing.
            </span>
          </p>
          <button
            onClick={onAddScene}
            disabled={isGenerating}
            className="mt-1.5 ml-5 text-[11px] font-medium text-amber-900 dark:text-amber-200 underline underline-offset-2 hover:no-underline"
          >
            Open the scene setup to fix it
          </button>
        </div>
      )}

      {/* Control bar */}
      <div className="flex items-center gap-1.5 flex-wrap px-3 py-2.5 border-t border-border/60 mt-2">
        {/* No output-type control: Studio is image-only, so a selector with a
            single option would look interactive while doing nothing. Add one
            here when video lands. */}
        {/* Not a bare list of scenes: a scene needs a product and a camera,
            and picking one from a dropdown left the other two to be discovered
            as errors. This opens the same three-step flow as starting from a
            scene, so there is one way to set one up. */}
        {briefs.length > 0 && !selectedBrief && (
          <button
            type="button"
            onClick={onAddScene}
            disabled={isGenerating}
            title="Add a scene — picks the product, scene and camera style together"
            className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-border bg-background text-xs font-medium hover:bg-muted disabled:opacity-40"
          >
            <FileText className="w-3.5 h-3.5" />
            Add scene
          </button>
        )}

        {config.allow_camera_picker && cameras.length > 0 && !selectedCamera && (
          <ComposerMenu
            icon={<Camera className="w-3.5 h-3.5" />}
            label={selectedCamera?.name || "Camera style"}
            title="Camera style — from your configured styles"
            variant="source"
            active={!!selectedCamera}
          >
            <DropdownMenuLabel className="text-xs">Camera style</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => onSettingsChange({ cameraConfigId: null })} className="gap-2 cursor-pointer">
              <span className="w-4">{!settings.cameraConfigId && <Check className="w-3.5 h-3.5" />}</span>
              <span className="text-muted-foreground">None</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {cameras.map((c) => (
              <DropdownMenuItem key={c.id} onClick={() => onSettingsChange({ cameraConfigId: settings.cameraConfigId === c.id ? null : c.id })} className="gap-2 cursor-pointer">
                <span className="w-4">{settings.cameraConfigId === c.id && <Check className="w-3.5 h-3.5" />}</span>
                <span className="truncate">{c.name}</span>
              </DropdownMenuItem>
            ))}
          </ComposerMenu>
        )}

        {/* Kept left of the corner so the floating chat widget can't cover it. */}

        {/* Sources above, render parameters below — the divider makes the
            two kinds of control read as different rather than a flat row. */}
        <span className="h-4 w-px bg-border mx-1" aria-hidden />

        {config.allow_model_picker && selectedModel && (
          <ComposerMenu label={selectedModel.label} title="Model (render parameter)" variant="parameter">
            <DropdownMenuLabel className="text-xs">Model</DropdownMenuLabel>
            {availableModels.map((m) => (
              <DropdownMenuItem
                key={m.id}
                onClick={() => onSettingsChange({ model: m.id })}
                className="gap-2 items-start cursor-pointer"
              >
                <span className="w-4 pt-0.5">{selectedModel.id === m.id && <Check className="w-3.5 h-3.5" />}</span>
                <span className="flex flex-col flex-1 min-w-0">
                  <span className="text-sm">{m.label}</span>
                  <span className="text-[10px] text-muted-foreground leading-tight">{m.blurb}</span>
                </span>
                {/* The tier carries its price. These three used to cost the
                    same, which meant Pro was free quality and Lite saved
                    nothing. */}
                <span className="text-[10px] text-muted-foreground tabular-nums pt-0.5 shrink-0">
                  ~{costWithModel(creditsPerImage, m.id) * settings.count} cr
                </span>
              </DropdownMenuItem>
            ))}
          </ComposerMenu>
        )}

        {/* Aspect / resolution / count */}
        <ComposerMenu
          icon={<Ratio className="w-3.5 h-3.5" />}
          label={`${settings.aspectRatio} · ${settings.resolution} · ${settings.count}`}
          title="Format, resolution and count (render parameters)"
          variant="parameter"
        >
          <DropdownMenuLabel className="text-xs">Aspect ratio</DropdownMenuLabel>
          {ASPECT_GROUPS.map((g) => (
            <div key={g.label}>
              <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                {g.label}
              </div>
              <div className="grid grid-cols-4 gap-1 px-1.5 pb-1">
                {g.ratios.map((r) => (
                  <button
                    key={r}
                    onClick={() => onSettingsChange({ aspectRatio: r })}
                    className={cn(
                      "text-[11px] py-1 rounded-md border transition-colors",
                      settings.aspectRatio === r
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/40",
                    )}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {config.allow_resolution_picker && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Resolution</DropdownMenuLabel>
              <div className="grid grid-cols-4 gap-1 px-1.5 pb-1">
                {availableSizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => onSettingsChange({ resolution: s })}
                    className={cn(
                      "text-[11px] py-1 rounded-md border transition-colors",
                      settings.resolution === s
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/40",
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs">Images per prompt</DropdownMenuLabel>
          <div className="grid grid-cols-4 gap-1 px-1.5 pb-2">
            {Array.from({ length: config.max_images_per_turn }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => onSettingsChange({ count: n })}
                className={cn(
                  "text-[11px] py-1 rounded-md border transition-colors",
                  settings.count === n
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/40",
                )}
              >
                {n}
              </button>
            ))}
          </div>
        </ComposerMenu>

        {sceneIncomplete.length > 0 ? (
          <span className="text-[10px] text-amber-600 dark:text-amber-500 pl-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3 shrink-0" />
            A scene needs {sceneIncomplete.join(" and ")}
          </span>
        ) : (
          <span className="text-[10px] text-muted-foreground tabular-nums pl-1">
            ~{costWithModel(creditsPerImage, settings.model) * settings.count} credits
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Composer dropdown trigger.
 *
 * Two visually distinct kinds of control:
 *  - "source"    pulls configured data out of the database (brief, camera
 *                style, product). Filled, and tinted once something is chosen,
 *                because these change *what* is generated.
 *  - "parameter" is a pure render setting (model, aspect, resolution, count).
 *                Outlined and muted: they change *how* it is rendered.
 *
 * Without the distinction every control looks alike, and it isn't obvious
 * which ones depend on data someone configured elsewhere.
 */
function ComposerMenu({
  label, icon, title, variant = "parameter", active = false, children,
}: {
  label: string;
  icon?: React.ReactNode;
  title: string;
  variant?: "source" | "parameter";
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          title={title}
          className={cn(
            "inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-medium transition-colors max-w-[180px]",
            variant === "source"
              ? active
                ? "bg-primary/10 text-foreground border border-primary/30 hover:bg-primary/15"
                : "bg-secondary hover:bg-accent border border-transparent"
              : "border border-border text-muted-foreground hover:text-foreground hover:border-primary/40",
          )}
        >
          {icon}
          <span className="truncate">{label}</span>
          <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 max-h-96 overflow-y-auto">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
